import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { IngredientIcon } from '@/components/ui/ingredient-icon';
import { useAllMenuItemsWithIngredients } from '@/hooks/useMenuItems';
import { useMenuCategories } from '@/hooks/useMenuCategories';
import { useMenuItemOptions } from '@/hooks/useMenuItemOptions';
import { getUnitGroup } from '@/lib/unit-utils';
import { formatPrice } from '@/lib/utils';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import type { MeasurementUnit } from '@/types/api.types';

const COLORS = [
  '#10b981', // green
  '#3b82f6', // blue
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ef4444', // red
  '#06b6d4', // cyan
  '#f97316', // orange
  '#ec4899', // pink
  '#84cc16', // lime
  '#14b8a6', // teal
];

interface IngredientsGraphDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  restaurantId: string;
}

interface ChartIngredient {
  name: string;
  nameAr: string;
  nameFr: string;
  emoji?: string;
  quantity: number;
  unit: MeasurementUnit;
  displayLabel: string;
  menuItemOptionId?: string | null;
}

const UNIT_GROUP_LABELS: Record<string, string> = {
  weight: 'weight',
  volume: 'volume',
  count: 'count',
};

export function IngredientsGraphDialog({
  open,
  onOpenChange,
  restaurantId,
}: IngredientsGraphDialogProps) {
  const { t, i18n } = useTranslation();
  const { data: menuItems, isLoading } = useAllMenuItemsWithIngredients(restaurantId);
  const { data: categories } = useMenuCategories(restaurantId);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | 'all'>('all');

  const filteredItems = useMemo(() => {
    if (!menuItems) return [];
    if (selectedCategoryId === 'all') return menuItems;
    return menuItems.filter((item) => item.categoryId === selectedCategoryId);
  }, [menuItems, selectedCategoryId]);

  // Count items per category
  const categoryCounts = useMemo(() => {
    if (!menuItems) return {};
    const counts: Record<string, number> = {};
    menuItems.forEach((item) => {
      const catId = item.categoryId || 'uncategorized';
      counts[catId] = (counts[catId] || 0) + 1;
    });
    return counts;
  }, [menuItems]);

  const getCategoryName = (categoryId: string | null) => {
    if (!categoryId || !categories) return t('menu.noCategory');
    const cat = categories.find((c) => c.id === categoryId);
    if (!cat) return t('menu.noCategory');
    return i18n.language === 'ar' ? cat.nameAr : cat.nameFr;
  };

  const getItemName = (item: { nameAr: string; nameFr: string }) => {
    return i18n.language === 'ar' ? item.nameAr : item.nameFr;
  };

  const getIngredientName = (ing: { nameAr: string; nameFr: string }) => {
    return i18n.language === 'ar' ? ing.nameAr : ing.nameFr;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-[95vw] max-h-[90vh] flex flex-col overflow-y-auto p-4 sm:p-6 gap-4">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{t('menu.ingredientsGraph')}</DialogTitle>
          <DialogDescription>{t('menu.ingredientsGraphDescription')}</DialogDescription>
        </DialogHeader>

        {/* Category Filter Badges */}
        <div className="flex flex-wrap gap-2 flex-shrink-0">
          <Badge
            variant={selectedCategoryId === 'all' ? 'default' : 'outline'}
            className="cursor-pointer"
            onClick={() => setSelectedCategoryId('all')}
          >
            {t('menu.allCategories')} ({menuItems?.length || 0})
          </Badge>
          {categories?.map((cat) => {
            const count = categoryCounts[cat.id] || 0;
            return (
              <Badge
                key={cat.id}
                variant={selectedCategoryId === cat.id ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setSelectedCategoryId(cat.id)}
              >
                {i18n.language === 'ar' ? cat.nameAr : cat.nameFr} ({count})
              </Badge>
            );
          })}
        </div>

        {/* Content - entire modal scrolls so graphs have room on mobile */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">{t('common.loading')}</p>
          </div>
        ) : !filteredItems || filteredItems.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">{t('menu.noIngredientsData')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 pb-4">
            {filteredItems.map((item) => (
              <PlateIngredientsCard
                key={item.id}
                menuItemId={item.id}
                name={getItemName(item)}
                price={item.price}
                categoryName={getCategoryName(item.categoryId)}
                ingredients={item.ingredients.map((ing) => ({
                  name: getIngredientName(ing.ingredient),
                  nameAr: ing.ingredient.nameAr,
                  nameFr: ing.ingredient.nameFr,
                  emoji: ing.ingredient.emoji,
                  quantity: ing.quantity,
                  unit: ing.unit,
                  displayLabel: `${ing.quantity} ${ing.unit}`,
                  menuItemOptionId: ing.menuItemOptionId,
                }))}
              />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PlateIngredientsCard({
  menuItemId,
  name,
  price,
  categoryName,
  ingredients,
}: {
  menuItemId: string;
  name: string;
  price: number;
  categoryName: string;
  ingredients: ChartIngredient[];
}) {
  const { t, i18n } = useTranslation();
  const { data: options } = useMenuItemOptions(menuItemId);

  // Group ingredients by unit group
  const groupedIngredients = useMemo(() => {
    const groups = new Map<string, ChartIngredient[]>();
    for (const ing of ingredients) {
      const group = getUnitGroup(ing.unit);
      const list = groups.get(group) || [];
      list.push(ing);
      groups.set(group, list);
    }
    return groups;
  }, [ingredients]);

  const hasMultipleGroups = groupedIngredients.size > 1;
  const chartHeight = ingredients.length * 50 + 60;

  // Helper to get option name
  const getOptionName = (optionId: string | null | undefined) => {
    if (!optionId || !options) return null;
    const option = options.find((o) => o.id === optionId);
    if (!option) return null;
    return i18n.language === 'ar' ? option.nameAr : option.nameFr;
  };

  // Build chart data with all ingredients
  const chartData = ingredients.map((ing) => ({
    name: ing.name,
    nameAr: ing.nameAr,
    nameFr: ing.nameFr,
    emoji: ing.emoji,
    quantity: ing.quantity,
    label: ing.displayLabel,
    optionName: getOptionName(ing.menuItemOptionId),
  }));

  // Custom Y-axis tick that renders ingredient icon and name (with option if applicable)
  const CustomYAxisTick = ({ x, y, payload }: any) => {
    const ing = chartData.find((d) => d.name === payload.value);
    if (!ing) return null;

    return (
      <g transform={`translate(${x},${y})`}>
        <foreignObject x={-160} y={-12} width={155} height={24}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              fontSize: '12px',
              color: '#666',
              height: '100%',
              gap: '6px',
            }}
          >
            <IngredientIcon
              ingredient={{
                nameAr: ing.nameAr,
                nameFr: ing.nameFr,
                ...(ing.emoji && { emoji: ing.emoji }),
              }}
              size={18}
            />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <span>{payload.value}</span>
              {ing.optionName && (
                <span style={{ fontSize: '10px', color: '#999', fontStyle: 'italic' }}>
                  ({ing.optionName})
                </span>
              )}
            </div>
          </div>
        </foreignObject>
      </g>
    );
  };

  return (
    <Card className="flex flex-col min-h-0">
      <CardHeader className="pb-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{name}</CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">
              {categoryName}
            </Badge>
            <Badge variant="outline" className="font-mono text-xs">
              {formatPrice(price)}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 min-h-0">
        {hasMultipleGroups ? (
          // Render separate charts per unit group
          <div className="space-y-3">
            {Array.from(groupedIngredients.entries()).map(([group, groupIngs]) => {
              const groupChartData = groupIngs.map((ing) => ({
                name: ing.name,
                nameAr: ing.nameAr,
                nameFr: ing.nameFr,
                emoji: ing.emoji,
                quantity: ing.quantity,
                label: ing.displayLabel,
                optionName: getOptionName(ing.menuItemOptionId),
              }));
              const groupHeight = groupIngs.length * 50 + 60;
              const groupLabel = UNIT_GROUP_LABELS[group];

              // Custom Y-axis tick for this group
              const GroupCustomYAxisTick = ({ x, y, payload }: any) => {
                const ing = groupChartData.find((d) => d.name === payload.value);
                if (!ing) return null;

                return (
                  <g transform={`translate(${x},${y})`}>
                    <foreignObject x={-160} y={-12} width={155} height={24}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          fontSize: '12px',
                          color: '#666',
                          height: '100%',
                          gap: '6px',
                        }}
                      >
                        <IngredientIcon
                          ingredient={{
                            nameAr: ing.nameAr,
                            nameFr: ing.nameFr,
                            ...(ing.emoji && { emoji: ing.emoji }),
                          }}
                          size={18}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <span>{payload.value}</span>
                          {ing.optionName && (
                            <span style={{ fontSize: '10px', color: '#999', fontStyle: 'italic' }}>
                              ({ing.optionName})
                            </span>
                          )}
                        </div>
                      </div>
                    </foreignObject>
                  </g>
                );
              };

              return (
                <div key={group}>
                  {groupLabel && (
                    <p className="text-xs text-muted-foreground mb-1 font-medium">
                      {t(`menu.${groupLabel}`)}
                    </p>
                  )}
                  <div style={{ height: groupHeight }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={groupChartData}
                        layout="vertical"
                        margin={{ left: 0, right: 50, top: 5, bottom: 5 }}
                      >
                        <XAxis type="number" hide />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={160}
                          tick={<GroupCustomYAxisTick />}
                        />
                        <Tooltip
                          formatter={(_: any, __: any, props: any) => [props.payload.label, '']}
                        />
                        <Bar
                          dataKey="quantity"
                          radius={[0, 4, 4, 0]}
                          label={{
                            position: 'right',
                            fontSize: 12,
                            fontWeight: 600,
                            content: (props: any) => {
                              const { x, y, width, index } = props;
                              const data = groupChartData[index];
                              return (
                                <text
                                  x={Number(x) + Number(width) + 5}
                                  y={y}
                                  dy={5}
                                  fill="#666"
                                  fontSize={12}
                                  fontWeight={600}
                                  textAnchor="start"
                                >
                                  {data?.label}
                                </text>
                              );
                            },
                          }}
                        >
                          {groupChartData.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length] || '#10b981'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // Single chart for all ingredients
          <div style={{ height: chartHeight }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ left: 0, right: 50, top: 5, bottom: 5 }}
              >
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={160} tick={<CustomYAxisTick />} />
                <Tooltip formatter={(_: any, __: any, props: any) => [props.payload.label, '']} />
                <Bar
                  dataKey="quantity"
                  radius={[0, 4, 4, 0]}
                  label={{
                    position: 'right',
                    fontSize: 12,
                    fontWeight: 600,
                    content: (props: any) => {
                      const { x, y, width, index } = props;
                      const data = chartData[index];
                      return (
                        <text
                          x={Number(x) + Number(width) + 5}
                          y={y}
                          dy={5}
                          fill="#666"
                          fontSize={12}
                          fontWeight={600}
                          textAnchor="start"
                        >
                          {data?.label}
                        </text>
                      );
                    },
                  }}
                >
                  {chartData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length] || '#10b981'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
